import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import { UsageProvider } from './contexts/UsageContext';
import HashRedirectHandler from './components/HashRedirectHandler';
import CookieConsent from './components/CookieConsent';
import Navbar from './components/common/Navbar';
import Footer from './components/Footer';
import Home from './pages/Home';
import Login from './pages/Login';
import LinkedInGuide from './pages/LinkedInGuide';
import BusinessNetworkingBasics from './pages/BusinessNetworkingBasics';
import FreeTrainingMaterials from './pages/FreeTrainingMaterials';
import TestTemplates from './pages/TestTemplates';
import Register from './pages/Register';
import IdeaGenerator from './pages/IdeaGenerator';
import IdeaChoice from './pages/IdeaChoice';
import AddIdea from './pages/AddIdea';
import SavedIdeas from './pages/SavedIdeas';
import TrialOnboarding from './pages/TrialOnboarding';
import BusinessPlan from './pages/BusinessPlan';
import BusinessPlanHub from './pages/business-plan/BusinessPlanHub';
import BusinessPlanShared from './pages/BusinessPlanShared';
import BusinessPlanInvite from './pages/BusinessPlanInvite';
import Launchpad from './pages/Launchpad';
import Marketing from './pages/Marketing';
import Finance from './pages/Finance';
import LegalHR from './pages/LegalHR';
import LegalHRLanding from './pages/modules/LegalHRLanding';
import PrivacyPolicyGenerator from './pages/legal-hr/PrivacyPolicyGenerator';
import TermsConditionsGenerator from './pages/legal-hr/TermsConditionsGenerator';
import EmployeeHandbookCreator from './pages/legal-hr/EmployeeHandbookCreator';
import NdaGenerator from './pages/legal-hr/NdaGenerator';
import ContractorAgreementGenerator from './pages/legal-hr/ContractorAgreementGenerator';
import HrPolicyGenerator from './pages/legal-hr/HrPolicyGenerator';
import ContractAnalyzer from './pages/legal-hr/ContractAnalyzer';
import StaffManagement from './pages/StaffManagement';
import Recruitment from './pages/Recruitment';
import ApplicationDetail from './pages/recruitment/ApplicationDetail';
import PublicJobBoard from './pages/recruitment/PublicJobBoard';
import PublicJobDetail from './pages/recruitment/PublicJobDetail';
// Business Name, Logo, Email Signature merged into Brand Kit Creator
import BrandKitCreator from './pages/marketing/BrandKitCreator';
import LinkedInHub from './pages/marketing/LinkedInHub';
// SocialPostGenerator removed - use SocialMediaPlanner instead
import SocialMediaPlanner from './pages/marketing/SocialMediaPlanner';
import TextToImageGenerator from './pages/marketing/TextToImageGenerator';
import TextToVideoGenerator from './pages/marketing/TextToVideoGenerator';
// VideoRecorderPage removed - non-functional
import SharedVideoWatchPage from './pages/SharedVideoWatchPage';
import PressReleaseGenerator from './pages/marketing/PressReleaseGenerator';
import SEOContentHub from './pages/marketing/SEOContentHub';
import SEOToolsHub from './pages/marketing/SEOToolsHub';
import EcommerceHub from './pages/marketing/EcommerceHub';
import PrintOnDemandGenerator from './pages/marketing/PrintOnDemandGenerator';
// AIModelProductLensGenerator removed
import AffiliateManagement from './pages/marketing/AffiliateManagement';
import SeoAnalyzer from './pages/marketing/SeoAnalyzer';
import WebTrafficAnalytics from './pages/marketing/WebTrafficAnalytics';
import Editor from './pages/website/Editor';
import TemplateLibrary from './pages/marketing/TemplateLibrary';
import AIWebsiteGenerator from './pages/marketing/AIWebsiteGenerator';
import VideoRecorderPage from './pages/VideoRecorderPage';
import LinkToolsHub from './pages/marketing/LinkToolsHub';
import AffiliateSignup from './pages/AffiliateSignup';
import AffiliateLogin from './pages/affiliate/AffiliateLogin';
import AffiliateDashboard from './pages/affiliate/AffiliateDashboard';
import AffiliateProfile from './pages/affiliate/AffiliateProfile';
import AffiliateTaxCompliance from './pages/affiliate/AffiliateTaxCompliance';
import EmailInterface from './pages/EmailInterface';
import ModernEmailInterface from './pages/ModernEmailInterface';
import ColdEmailDocs from './pages/ColdEmailDocs';
import EmailDashboard from './pages/EmailDashboard';
import EmailCampaigns from './pages/EmailCampaigns';
import EmailContacts from './pages/EmailContacts';
import EmailMailboxes from './pages/EmailMailboxes';
import EmailABTestDetail from './components/email/analytics/EmailABTestDetail';
import EmailABTestCreate from './components/email/analytics/EmailABTestCreate';
import EmailTemplates from './pages/EmailTemplates';
import CampaignContacts from './pages/CampaignContacts';
import EmailUnifiedInbox from './pages/EmailUnifiedInbox';
import PitchDeck from './pages/PitchDeck';
import PitchDeckGenerator from './pages/PitchDeckGenerator';
import PriceCalculator from './pages/finance/PriceCalculator';
import ProfitMarginAnalyzer from './pages/finance/ProfitMarginAnalyzer';
import BreakEvenCalculator from './pages/finance/BreakEvenCalculator';
import CashFlowForecaster from './pages/finance/CashFlowForecaster';
import PricingStrategyAdvisor from './pages/finance/PricingStrategyAdvisor';
import PricingHub from './pages/pricing-hub/PricingHub';
import ForecastingHub from './pages/forecasting-hub/ForecastingHub';
import SupplierFinder from './pages/finance/SupplierFinder';
import RiskManager from './pages/finance/RiskManager';
import SalesTracker from './pages/finance/SalesTracker';
import ExpenseTracker from './pages/finance/ExpenseTracker';
import FinanceDoctor from './pages/finance/FinanceDoctor';
import FinanceHub from './pages/finance-hub/FinanceHub';
import University from './pages/University';
import EntrepreneurialMindset from './pages/EntrepreneurialMindset';
import Resilience from './pages/Resilience';
import Adaptability from './pages/Adaptability';
import Vision from './pages/Vision';
import AboutUs from './pages/AboutUs';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import RefundPolicy from './pages/RefundPolicy';
import ContactUs from './pages/ContactUs';
import DisclaimerPage from './pages/Disclaimer';
import ResetPassword from './pages/ResetPassword';
import ResetPasswordConfirm from './pages/ResetPasswordConfirm';
import AccountPage from './pages/account/AccountPage';
import CompanySignup from './pages/CompanySignup';
import CompanyOnboarding from './pages/CompanyOnboarding';
import TeamManagement from './pages/TeamManagement';
import AuthGuard from './components/auth/AuthGuard';
import IdeaLanding from './pages/IdeaLanding';
import IdeaPersonaLanding from './pages/IdeaPersonaLanding';
import PersonaIdeasIndex from './pages/PersonaIdeasIndex';
import BusinessIdeasIndex from './pages/BusinessIdeasIndex';
import ExplanatumVsIdeaBuddy from './pages/comparison/ExplanatumVsIdeaBuddy';
import ExplanatumVsBizplan from './pages/comparison/ExplanatumVsBizplan';
import ExplanatumVsCanvanizer from './pages/comparison/ExplanatumVsCanvanizer';
import UniversityContentManager from './pages/admin/UniversityContentManager';
import ContentDetail from './pages/university/ContentDetail';
import PaymentPage from './pages/payment/PaymentPage';
import PaymentSuccessPage from './pages/payment/PaymentSuccessPage';
import GuestCheckout from './pages/payment/GuestCheckout';
import CheckoutSuccessPage from './pages/payment/CheckoutSuccessPage';
import BusinessConsultantHub from './pages/business-consultant/BusinessConsultantHub';
import SignupPage from './pages/SignupPage';
import AuthCallback from './pages/AuthCallback';
import ComplianceDashboard from './pages/admin/ComplianceDashboard';
import QueueAdminPanel from './pages/admin/QueueAdminPanel';
import IntegrationsDashboard from './pages/admin/IntegrationsDashboard';
import SupportDeskDashboard from './pages/admin/SupportDeskDashboard';
import DSRRequest from './pages/DSRRequest';
import DataRetentionPolicy from './pages/DataRetentionPolicy';
import CookiePolicy from './pages/CookiePolicy';
import Notifications from './pages/Notifications';
import DigestRunPage from './pages/DigestRunPage';
import BusinessPlanLanding from './pages/modules/BusinessPlanLanding';
import BrandKitCreatorLanding from './pages/modules/BrandKitCreatorLanding';
import BusinessConsultantLanding from './pages/modules/BusinessConsultantLanding';
import LinkedInEnhancerLanding from './pages/modules/LinkedInEnhancerLanding';
import RecruitmentLanding from './pages/modules/RecruitmentLanding';
import PressReleaseLanding from './pages/modules/PressReleaseLanding';
import SocialMediaPlannerLanding from './pages/modules/SocialMediaPlannerLanding';
import ColdEmailLanding from './pages/modules/ColdEmailLanding';
import AffiliateMarketingLanding from './pages/modules/AffiliateMarketingLanding';
import LinkIntelligenceLanding from './pages/modules/LinkIntelligenceLanding';
import FinancialPlanningLanding from './pages/modules/FinancialPlanningLanding';
import PricingIntelligenceLanding from './pages/modules/PricingIntelligenceLanding';
import RevenueOperationsLanding from './pages/modules/RevenueOperationsLanding';
import SupplierOperationsLanding from './pages/modules/SupplierOperationsLanding';
import RiskManagementLanding from './pages/modules/RiskManagementLanding';
import ProjectManagementLanding from './pages/modules/ProjectManagementLanding';
import InvestorCrmLanding from './pages/modules/InvestorCrmLanding';
import SEOContentHubLanding from './pages/modules/SEOContentHubLanding';
import SEOToolsHubLanding from './pages/modules/SEOToolsHubLanding';
import EcommerceHubLanding from './pages/modules/EcommerceHubLanding';
import CRMLanding from './pages/modules/CRMLanding';
import StaffManagementLanding from './pages/modules/StaffManagementLanding';
import SupportDeskLanding from './pages/modules/SupportDeskLanding';
import ModuleGuide from './pages/ModuleGuide';
import HelpCenter from './pages/HelpCenter';
// EmailSignatureCreator merged into Brand Kit Creator
import Notepad from './pages/tools/Notepad';
import FileManager from './pages/tools/FileManager';
import { setupPdfJsWorker } from './utils/setupPdfWorker';
import { registerServiceWorker } from './utils/pwaRegistration';
import InstallPWAPrompt from './components/pwa/InstallPWAPrompt';
// CRM pages
import CRMLayout from './components/crm/CRMLayout';
import CrmLayoutWithMetrics from './components/crm/CrmLayoutWithMetrics';
import CustomerCrmHub from './pages/crm/CustomerCrmHub';
import ContactDetailEnhanced from './pages/crm/ContactDetailEnhanced';
import CompanyDetail from './pages/crm/CompanyDetail';
import CompanySitesManager from './pages/crm/CompanySitesManager';
import DealDetail from './pages/crm/DealDetail';
import ActivityDetail from './pages/crm/ActivityDetail';
import MyTasks from './pages/crm/MyTasks';
import QuoteBuilder from './pages/crm/QuoteBuilder';
import ProductCatalog from './pages/crm/ProductCatalog';
import SupplierCatalog from './pages/crm/SupplierCatalog';
import TelemarketingCampaigns from './pages/crm/TelemarketingCampaigns';
import CreateTelemarketingCampaign from './pages/crm/CreateTelemarketingCampaign';
import TelemarketingCampaignDetail from './pages/crm/TelemarketingCampaignDetail';
import CampaignCallMonitor from './pages/crm/CampaignCallMonitor';
import CampaignAnalytics from './pages/crm/CampaignAnalytics';
import CallDetail from './pages/crm/CallDetail';
import SupplierHub from './pages/supplier-hub/SupplierHub';
import ProjectManagementHub from './pages/project-management/ProjectManagementHub';
import SupplierCrmLayout from './components/supplier-crm/SupplierCrmLayout';
import SupplierCrmSuppliersList from './pages/supplier-crm/SuppliersList';
import SupplierCrmQuotesList from './pages/supplier-crm/QuotesList';
import SupplierCrmContactsList from './pages/supplier-crm/ContactsList';
import SupplierSolutionsPage from './pages/supplier-crm/SupplierSolutions';
import InvestorCrmHub from './pages/investor-crm/InvestorCrmHub';
// Lazy-load heavy CRM pages
const DealsBoard = lazy(() => import('./pages/crm/DealsBoard'));
const Activities = lazy(() => import('./pages/crm/Activities'));
const Dashboards = lazy(() => import('./pages/crm/Dashboards'));
const Workflows = lazy(() => import('./pages/crm/Workflows'));
const MeetingsCalendar = lazy(() => import('./pages/crm/MeetingsCalendar'));
const SchedulingSettings = lazy(() => import('./pages/crm/SchedulingSettings'));
const PublicBooking = lazy(() => import('./pages/PublicBooking'));

// Lazy load the SocialMediaPlanner for better performance
const LazySocialMediaPlanner = lazy(() => import('./pages/marketing/SocialMediaPlanner'));

const App: React.FC = () => {
  // Initialize pdf.js worker once
  useEffect(() => {
    setupPdfJsWorker().catch(() => {
      // non-blocking: keep PDF extraction best-effort
      console.warn('pdf.js worker setup failed; continuing without worker');
    });
  }, []);

  // Initialize PWA service worker
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') {
      registerServiceWorker().catch((error) => {
        console.warn('Service worker registration failed:', error);
      });
    }
  }, []);

  return (
    <Router>
      <AuthProvider>
        <UsageProvider>
          <HashRedirectHandler />
          <InstallPWAPrompt />
          <div className="min-h-screen bg-gray-100">
            <Navbar />
          <main className="py-10">
            <div className="px-4 sm:px-6 lg:px-8">
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<SignupPage />} />
                <Route path="/company-signup" element={<CompanySignup />} />
                <Route path="/company/onboarding" element={<CompanyOnboarding />} />
                <Route path="/team" element={<TeamManagement />} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/reset-password-confirm" element={<ResetPasswordConfirm />} />
                <Route
                  path="/onboarding/trial"
                  element={
                    <AuthGuard>
                      <TrialOnboarding />
                    </AuthGuard>
                  }
                />
                <Route path="/affiliate/signup/:formId" element={<AffiliateSignup />} />
                <Route path="/affiliate/signup" element={<AffiliateSignup />} />
                <Route path="/affiliate-signup" element={<AffiliateSignup />} />
                <Route path="/affiliate-signup/:programId" element={<AffiliateSignup />} />
                
                {/* Affiliate Portal Routes - These use affiliate user ID authentication, not JWT */}
                <Route path="/affiliate/login" element={<AffiliateLogin />} />
                <Route path="/admin/compliance" element={<ComplianceDashboard />} />
                <Route path="/affiliate/profile" element={<AffiliateProfile />} />
                <Route path="/affiliate/tax" element={<AffiliateTaxCompliance />} />
                <Route
                  path="/notifications"
                  element={
                    <AuthGuard>
                      <Notifications />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/digests/:id"
                  element={
                    <AuthGuard>
                      <DigestRunPage />
                    </AuthGuard>
                  }
                />
                {/* Public Privacy Ops */}
                <Route path="/privacy/dsr" element={<DSRRequest />} />
                <Route path="/privacy/retention-policy" element={<DataRetentionPolicy />} />
                <Route path="/privacy/cookie-policy" element={<CookiePolicy />} />
                <Route
                  path="/idea-generator"
                  element={<IdeaGenerator />}
                />
                <Route
                  path="/idea-choice"
                  element={
                    <AuthGuard>
                      <IdeaChoice />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/add-idea"
                  element={
                    <AuthGuard>
                      <AddIdea />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/saved-ideas"
                  element={
                    <AuthGuard>
                      <SavedIdeas />
                    </AuthGuard>
                  }
                />
                <Route path="/business-plan/invite/:token" element={<BusinessPlanInvite />} />
                <Route path="/business-plan/shared/:token" element={<BusinessPlanShared />} />
                <Route
                  path="/business-plan"
                  element={
                    <AuthGuard>
                      <BusinessPlanHub />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/business-plan/new"
                  element={
                    <AuthGuard>
                      <BusinessPlanHub />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/business-plan/:id"
                  element={
                    <AuthGuard>
                      <BusinessPlanHub />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/pitch-deck"
                  element={
                    <AuthGuard>
                      <PitchDeckGenerator />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/business-consultant"
                  element={
                    <AuthGuard>
                      <BusinessConsultantHub />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/business-consultant-legacy"
                  element={<Navigate to="/business-consultant" replace />}
                />
                <Route
                  path="/launchpad"
                  element={
                    <AuthGuard>
                      <Launchpad />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/marketing"
                  element={
                    <AuthGuard>
                      <Marketing />
                    </AuthGuard>
                  }
                />
                {/* Brand Kit Creator - Main Hub */}
                <Route
                  path="/marketing/brand-kit-creator"
                  element={
                    <AuthGuard>
                      <BrandKitCreator />
                    </AuthGuard>
                  }
                />
                {/* Individual tool redirects - all accessible independently */}
                <Route path="/marketing/business-name-generator" element={<Navigate to="/marketing/brand-kit-creator#business-name" replace />} />
                <Route path="/marketing/letterhead-creator" element={<Navigate to="/marketing/brand-kit-creator#letterhead" replace />} />
                <Route path="/marketing/compliments-slip-generator" element={<Navigate to="/marketing/brand-kit-creator#compliments-slip" replace />} />
                {/* Unified LinkedIn Hub */}
                <Route
                  path="/linkedin-hub"
                  element={
                    <AuthGuard>
                      <LinkedInHub />
                    </AuthGuard>
                  }
                />
                
                {/* Legacy LinkedIn Routes - DEPRECATED (redirects to LinkedIn Hub) */}
                <Route path="/marketing/linkedin-profile-enhancer" element={<Navigate to="/linkedin-hub#personal" replace />} />
                {/* Social Post Generator removed - redirect to Social Media Planner */}
                <Route path="/marketing/social-post-generator" element={<Navigate to="/marketing/social-media-planner" replace />} />
                {/* Logo Generator redirect - now part of Brand Kit Creator */}
                <Route path="/marketing/logo-generator" element={<Navigate to="/marketing/brand-kit-creator#logo-design" replace />} />
                <Route path="/marketing/linkedin-business-profile-enhancer" element={<Navigate to="/linkedin-hub#business" replace />} />
                {/* Video Recorder route removed - non-functional */}
                <Route
                  path="/videos/watch/:videoId"
                  element={<SharedVideoWatchPage />}
                />
                <Route
                  path="/planner"
                  element={
                    <AuthGuard>
                      <Suspense fallback={
                        <div className="flex items-center justify-center h-64">
                          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                        </div>
                      }>
                        <LazySocialMediaPlanner />
                      </Suspense>
                    </AuthGuard>
                  }
                />
                <Route
                  path="/marketing/text-to-image-generator"
                  element={
                    <AuthGuard>
                      <TextToImageGenerator />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/marketing/text-to-video-generator"
                  element={
                    <AuthGuard>
                      <TextToVideoGenerator />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/marketing/press-release-generator"
                  element={
                    <AuthGuard>
                      <PressReleaseGenerator />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/marketing/print-on-demand"
                  element={
                    <AuthGuard>
                      <PrintOnDemandGenerator />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/marketing/affiliate-management"
                  element={
                    <AuthGuard>
                      <AffiliateManagement />
                    </AuthGuard>
                  }
                />
                {/* Unified SEO Content Hub */}
                <Route
                  path="/marketing/seo-content-hub"
                  element={
                    <AuthGuard>
                      <SEOContentHub />
                    </AuthGuard>
                  }
                />
                
                {/* Legacy SEO Content Routes - DEPRECATED (redirects to SEO Content Hub) */}
                <Route path="/marketing/blog-post-creator" element={<Navigate to="/marketing/seo-content-hub#blog-creator" replace />} />
                <Route path="/marketing/topical-authority-generator" element={<Navigate to="/marketing/seo-content-hub#topical-authority" replace />} />
                
                {/* Unified SEO Tools Hub */}
                <Route
                  path="/marketing/seo-tools-hub"
                  element={
                    <AuthGuard>
                      <SEOToolsHub />
                    </AuthGuard>
                  }
                />
                
                {/* Legacy SEO Tools Routes - DEPRECATED (redirects to SEO Tools Hub) */}
                <Route path="/marketing/seo-analyzer" element={<Navigate to="/marketing/seo-tools-hub#seo-analyzer" replace />} />
                <Route path="/marketing/web-traffic-analytics" element={<Navigate to="/marketing/seo-tools-hub#web-traffic" replace />} />
                
                {/* Unified Ecommerce Hub */}
                <Route
                  path="/marketing/ecommerce-hub"
                  element={
                    <AuthGuard>
                      <EcommerceHub />
                    </AuthGuard>
                  }
                />
                
                <Route
                  path="/marketing/social-media-planner"
                  element={
                    <AuthGuard>
                      <SocialMediaPlanner />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/marketing/ai-website-generator"
                  element={
                    <AuthGuard>
                      <AIWebsiteGenerator />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/website/editor"
                  element={
                    <AuthGuard>
                      <Editor />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/marketing/video-recorder"
                  element={
                    <AuthGuard>
                      <VideoRecorderPage />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/website/editor/:siteId"
                  element={
                    <AuthGuard>
                      <Editor />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/marketing/templates"
                  element={
                    <AuthGuard>
                      <TemplateLibrary />
                    </AuthGuard>
                  }
                />
                {/* Unified Link Tools Hub */}
                <Route
                  path="/link-tools-hub"
                  element={
                    <AuthGuard>
                      <LinkToolsHub />
                    </AuthGuard>
                  }
                />
                
                {/* Legacy Marketing Routes - DEPRECATED (redirects to Link Tools Hub) */}
                <Route path="/marketing/url-shortener" element={<Navigate to="/link-tools-hub#shortener" replace />} />
                <Route path="/marketing/qr-code-generator" element={<Navigate to="/link-tools-hub#qrcode" replace />} />
                <Route path="/marketing/my-links" element={<Navigate to="/link-tools-hub#mylinks" replace />} />
                {/* AIModelProductLensGenerator route removed */}
                {/* Admin Compliance */}
                <Route
                  path="/admin/compliance"
                  element={
                    <AuthGuard>
                      <ComplianceDashboard />
                    </AuthGuard>
                  }
                />
                {/* Admin Integrations */}
                <Route
                  path="/admin/integrations"
                  element={
                    <AuthGuard>
                      <IntegrationsDashboard />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/admin/support"
                  element={
                    <AuthGuard>
                      <SupportDeskDashboard />
                    </AuthGuard>
                  }
                />
                <Route path="/help" element={<HelpCenter />} />
                <Route path="/help/:slug" element={<HelpCenter />} />
                {/* Admin Queue Ops */}
                <Route
                  path="/admin/queue"
                  element={
                    <AuthGuard>
                      <QueueAdminPanel />
                    </AuthGuard>
                  }
                />
                {/* Cold Email Tool Routes */}
                <Route
                  path="/email/*"
                  element={
                    <AuthGuard>
                      <ModernEmailInterface />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/cold-email-docs"
                  element={
                    <AuthGuard>
                      <ColdEmailDocs />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/email/campaigns/:id/contacts"
                  element={
                    <AuthGuard>
                      <CampaignContacts />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/email/ab-tests/:testId"
                  element={
                    <AuthGuard>
                      <EmailABTestDetail />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/email/ab-tests/new"
                  element={
                    <AuthGuard>
                      <EmailABTestCreate />
                    </AuthGuard>
                  }
                />
                {/* Unified inbox is now integrated into EmailInterface as a tab */}
                {/* Finance Routes */}
                <Route
                  path="/finance"
                  element={
                    <AuthGuard>
                      <Finance />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/legal-hr"
                  element={
                    <AuthGuard>
                      <LegalHR />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/legal-hr/privacy-policy-generator"
                  element={
                    <AuthGuard>
                      <PrivacyPolicyGenerator />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/legal-hr/terms-conditions-generator"
                  element={
                    <AuthGuard>
                      <TermsConditionsGenerator />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/legal-hr/employee-handbook-creator"
                  element={
                    <AuthGuard>
                      <EmployeeHandbookCreator />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/legal-hr/nda-generator"
                  element={
                    <AuthGuard>
                      <NdaGenerator />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/legal-hr/contractor-agreement-generator"
                  element={
                    <AuthGuard>
                      <ContractorAgreementGenerator />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/legal-hr/hr-policy-generator"
                  element={
                    <AuthGuard>
                      <HrPolicyGenerator />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/legal-hr/contract-analyzer"
                  element={
                    <AuthGuard>
                      <ContractAnalyzer />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/legal-hr/staff-management"
                  element={
                    <AuthGuard>
                      <StaffManagement />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/recruitment/applications/:id"
                  element={
                    <AuthGuard>
                      <ApplicationDetail />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/recruitment"
                  element={
                    <AuthGuard>
                      <Recruitment />
                    </AuthGuard>
                  }
                />
                
                {/* Public Job Board - No Auth Required */}
                <Route path="/careers" element={<PublicJobBoard />} />
                <Route path="/careers/jobs/:id" element={<PublicJobDetail />} />
                
                {/* Placeholder routes for finance tools - components to be created */}
                <Route
                  path="/finance/price-calculator"
                  element={
                    <AuthGuard>
                      <PriceCalculator />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/finance/profit-margin"
                  element={
                    <AuthGuard>
                      <ProfitMarginAnalyzer />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/finance/break-even"
                  element={
                    <AuthGuard>
                      <BreakEvenCalculator />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/finance/cash-flow"
                  element={
                    <AuthGuard>
                      <CashFlowForecaster />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/finance/pricing-strategy"
                  element={
                    <AuthGuard>
                      <PricingStrategyAdvisor />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/finance/supplier-finder"
                  element={
                    <AuthGuard>
                      <SupplierFinder />
                    </AuthGuard>
                  }
                />
                {/* Unified Finance Hub */}
                <Route
                  path="/finance-hub"
                  element={
                    <AuthGuard>
                      <FinanceHub />
                    </AuthGuard>
                  }
                />
                
                {/* Unified Pricing Hub */}
                <Route
                  path="/pricing-hub"
                  element={
                    <AuthGuard>
                      <PricingHub />
                    </AuthGuard>
                  }
                />
                
                {/* Unified Forecasting Hub */}
                <Route
                  path="/forecasting-hub"
                  element={
                    <AuthGuard>
                      <ForecastingHub />
                    </AuthGuard>
                  }
                />
                
                {/* Legacy Finance Routes - DEPRECATED (redirects to hubs) */}
                <Route path="/finance/sales-tracker" element={<Navigate to="/finance-hub#transactions" replace />} />
                <Route path="/finance/expense-tracker" element={<Navigate to="/finance-hub#transactions" replace />} />
                <Route path="/finance/doctor" element={<Navigate to="/finance-hub#insights" replace />} />
                <Route path="/finance/price-calculator" element={<Navigate to="/pricing-hub#calculator" replace />} />
                <Route path="/finance/profit-margin" element={<Navigate to="/pricing-hub#margin" replace />} />
                <Route path="/finance/pricing-strategy" element={<Navigate to="/pricing-hub#strategy" replace />} />
                <Route path="/finance/break-even" element={<Navigate to="/forecasting-hub#breakeven" replace />} />
                <Route path="/finance/cash-flow" element={<Navigate to="/forecasting-hub#cashflow" replace />} />
                
                <Route
                  path="/finance/risk-manager"
                  element={
                    <AuthGuard>
                      <RiskManager />
                    </AuthGuard>
                  }
                />
                <Route path="/about-us" element={<AboutUs />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-of-service" element={<TermsOfService />} />
                <Route path="/refund-policy" element={<RefundPolicy />} />
                <Route path="/disclaimer" element={<DisclaimerPage />} />
                <Route path="/contact-us" element={<ContactUs />} />
                <Route path="/linkedin-guide" element={<LinkedInGuide />} />
                <Route path="/business-networking-basics" element={<BusinessNetworkingBasics />} />
                <Route path="/free-training" element={<FreeTrainingMaterials />} />
                <Route path="/test-templates" element={<TestTemplates />} />
                {/* SEO Programmatic Landing Pages for Idea Generator */}
                <Route path="/business-ideas" element={<BusinessIdeasIndex />} />
                {/* Module Landing Pages (public) */}
                <Route path="/modules/business-plan" element={<BusinessPlanLanding />} />
                <Route path="/modules/brand-kit-creator" element={<BrandKitCreatorLanding />} />
                <Route path="/modules/logo-generator" element={<Navigate to="/modules/brand-kit-creator" replace />} />
                <Route path="/modules/business-consultant" element={<BusinessConsultantLanding />} />
                <Route path="/modules/linkedin-enhancer" element={<LinkedInEnhancerLanding />} />
                <Route path="/modules/recruitment" element={<RecruitmentLanding />} />
                <Route path="/modules/press-release-generator" element={<PressReleaseLanding />} />
                <Route path="/modules/social-media-planner" element={<SocialMediaPlannerLanding />} />
                <Route path="/modules/cold-email" element={<ColdEmailLanding />} />
                <Route path="/modules/affiliate-marketing" element={<AffiliateMarketingLanding />} />
                <Route path="/modules/link-intelligence" element={<LinkIntelligenceLanding />} />
                <Route path="/modules/financial-planning" element={<FinancialPlanningLanding />} />
                <Route path="/modules/pricing-intelligence" element={<PricingIntelligenceLanding />} />
                <Route path="/modules/revenue-operations" element={<RevenueOperationsLanding />} />
                <Route path="/modules/supplier-operations" element={<SupplierOperationsLanding />} />
                <Route path="/modules/risk-management" element={<RiskManagementLanding />} />
                <Route path="/modules/project-management" element={<ProjectManagementLanding />} />
                <Route path="/modules/investor-crm" element={<InvestorCrmLanding />} />
                <Route path="/modules/legal-hr" element={<LegalHRLanding />} />
                <Route path="/modules/staff-management" element={<StaffManagementLanding />} />
                <Route path="/modules/customer-crm" element={<CRMLanding />} />
                <Route path="/modules/seo-content-hub" element={<SEOContentHubLanding />} />
                <Route path="/modules/seo-tools-hub" element={<SEOToolsHubLanding />} />
                <Route path="/modules/ecommerce-hub" element={<EcommerceHubLanding />} />
                <Route path="/modules/support-desk" element={<SupportDeskLanding />} />
                <Route path="/module-guide" element={<ModuleGuide />} />
                {/* Skill-based landing pages */}
                <Route path="/business-ideas/:skillsSlug/:timeSlug/:locationSlug" element={<IdeaLanding />} />
                {/* Persona-based landing pages */}
                <Route path="/business-ideas/for" element={<PersonaIdeasIndex />} />
                <Route path="/business-ideas/for/:persona/:timeSlug/:locationSlug" element={<IdeaPersonaLanding />} />
                
                {/* Payment Routes */}
                <Route path="/payment" element={<AuthGuard><PaymentPage /></AuthGuard>} />
                <Route path="/payment/success" element={<AuthGuard><PaymentSuccessPage /></AuthGuard>} />
                
                {/* Pitch Deck Route */}
                <Route
                  path="/pitch-deck"
                  element={
                    <AuthGuard>
                      <PitchDeck />
                    </AuthGuard>
                  }
                />
                {/* Tools */}
                {/* Email Signature Creator redirect - now part of Brand Kit Creator */}
                <Route path="/tools/email-signature" element={<Navigate to="/marketing/brand-kit-creator#email-signature" replace />} />
                <Route
                  path="/tools/notepad"
                  element={
                    <AuthGuard>
                      <Notepad />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/tools/files"
                  element={
                    <AuthGuard>
                      <FileManager />
                    </AuthGuard>
                  }
                />
                
                {/* Guest Checkout Routes - No Auth Required */}
                <Route path="/checkout/guest" element={<GuestCheckout />} />
                <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
                <Route path="/account" element={<AuthGuard><AccountPage /></AuthGuard>} />
                {/* Cash Flow and Pricing Strategy are marked as Coming Soon in the Finance page */}
                {/* Removed Invoice Generator and Expense Categorizer routes */}

                {/* Comparison Pages */}
                <Route path="/comparison/explanatum-vs-ideabuddy" element={<ExplanatumVsIdeaBuddy />} />
                <Route path="/comparison/explanatum-vs-bizplan" element={<ExplanatumVsBizplan />} />
                <Route path="/comparison/explanatum-vs-canvanizer" element={<ExplanatumVsCanvanizer />} />

                {/* Customer CRM - All routes with persistent metrics header */}
                <Route
                  path="/crm"
                  element={
                    <AuthGuard>
                      <CrmLayoutWithMetrics />
                    </AuthGuard>
                  }
                >
                  {/* CRM Hub (main dashboard) */}
                  <Route index element={<CustomerCrmHub />} />
                  
                  {/* CRM Detail Pages */}
                  <Route path="contacts/:contactId" element={<ContactDetailEnhanced />} />
                  <Route path="companies/:companyId" element={<CompanyDetail />} />
                  <Route path="companies/:companyId/sites" element={<CompanySitesManager />} />
                  <Route path="deals/:dealId" element={<DealDetail />} />
                  <Route path="deals/:dealId/quotes/new" element={<QuoteBuilder />} />
                  <Route path="deals/:dealId/quotes/:quoteId" element={<QuoteBuilder />} />
                  <Route path="activities/:activityId" element={<ActivityDetail />} />
                  <Route path="tasks" element={<MyTasks />} />
                  <Route path="products" element={<ProductCatalog />} />
                  <Route path="suppliers" element={<SupplierCatalog />} />
                  <Route path="telemarketing" element={<TelemarketingCampaigns />} />
                  <Route path="telemarketing/create" element={<CreateTelemarketingCampaign />} />
                  <Route path="telemarketing/campaigns/:campaignId" element={<TelemarketingCampaignDetail />} />
                  <Route path="telemarketing/:campaignId/calls" element={<CampaignCallMonitor />} />
                  <Route path="telemarketing/:campaignId/analytics" element={<CampaignAnalytics />} />
                  <Route path="telemarketing/calls/:callId" element={<CallDetail />} />
                  <Route path="scheduling" element={<Suspense fallback={<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div></div>}><SchedulingSettings /></Suspense>} />
                </Route>

                {/* Public Booking Page (No Auth Required) */}
                <Route path="/book/:slug" element={<Suspense fallback={<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div></div>}><PublicBooking /></Suspense>} />

                {/* Investor CRM Hub */}
                <Route
                  path="/investor-crm"
                  element={
                    <AuthGuard>
                      <InvestorCrmHub />
                    </AuthGuard>
                  }
                />

                {/* Unified Supplier Operations Hub - NEW */}
                <Route
                  path="/supplier-hub"
                  element={
                    <AuthGuard>
                      <SupplierHub />
                    </AuthGuard>
                  }
                />

                {/* Project Management Hub */}
                <Route
                  path="/project-management"
                  element={
                    <AuthGuard>
                      <ProjectManagementHub />
                    </AuthGuard>
                  }
                />

                {/* Legacy Supplier CRM Routes - DEPRECATED (redirects to hub) */}
                <Route path="/supplier-crm/*" element={<Navigate to="/supplier-hub#suppliers" replace />} />
                <Route path="/finance/supplier-finder" element={<Navigate to="/supplier-hub#discover" replace />} />


              </Routes>
            </div>
          </main>
          <Footer /> 
          {/* Add Footer component */}
          <CookieConsent privacyPolicyUrl="/privacy-policy" />
        </div>
        </UsageProvider>
      </AuthProvider>
    </Router>
  );
};

export default App;
